import {
  Component,
  EventEmitter,
  Output,
  Input,
  SimpleChanges,
  ViewChild,
  ElementRef,
  Renderer2,
  HostListener,
} from '@angular/core';
import { VendorService } from '@services/vendor.service';
import { Subscription } from 'rxjs';
import { AuthService } from '@core/auth.service';
import { MonthYearService } from '@services/monthyear.service';
import {
  ErrorMessages,
  ButtonLabels,
  LEGEND_ITEMS,
  LEAVE_TYPE_LIST,
  LeaveType,
  Attendance_Items,
} from '@constants/constants';
import { ERROR_MESSAGES, SUMMARY_KEYS } from '@constants/vendor-constants';
import { SummaryItem, LeaveDetails } from '@interfaces/vendor.model';
import { EmeraldDatepickerComponent } from '@emerald-angular/datepicker';
import {
  HeaderText,
  LeaveTypeEnum,
  PersonaType,
  rtoTypeEnum,
} from '@constants/global.enum';
import { TimesheetCalendarHelper } from './timesheet-calendar.helper';
import { CommonModule } from '@angular/common';
import { EmeraldIconModule } from '@emerald-angular/icon';
import { EmeraldButtonModule } from '@emerald-angular/button';
import {
  DateRange,
  MatCalendar,
  MatDatepickerModule,
} from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { icon_fontSize } from '@pages/vendor-management/timesheet/timesheet-config';
import { FormsModule } from '@angular/forms';
import { dateFormat } from '@constants/dateFormat';
import {
  LeaveCommentsResponse,
  LeaveComment,
} from '@interfaces/vendor-management-model';
import { TimesheetCalendarService } from './timesheet-calendar.service';
import { ToasterService } from '@services/toaster.service';
import { TIMESHEET_CONSTANTS } from '../timesheet/timesheet.constants';
import { RTO_CALENDAR_LEGEND } from '@constants/constants';
import { SUMMARY_KEYS_ATTENDANCE } from '@constants/vendor-constants';
import { BillingHighlightService } from '@shared/services/billing-highlight.service';

interface MarkedDate {
  date: string;
  type: string;
  label: string;
}

export interface CalendarLegendItem {
  label: string;
  class: string;
}

@Component({
  selector: 'app-timesheet-calendar',
  standalone: true,
  imports: [
    CommonModule,
    EmeraldIconModule,
    EmeraldButtonModule,
    MatDatepickerModule,
    MatNativeDateModule,
    FormsModule,
  ],
  templateUrl: './timesheet-calendar.component.html',
  styleUrls: ['./timesheet-calendar.component.scss'],
})
export class TimesheetCalendarComponent {
  @ViewChild('calendar', { static: false }) calendar!: MatCalendar<Date>;
  @Input() candidateDetails: {
    employeeId: string;
    employeeName: string;
    contractorName: string;
    jobTitle: string;
    location: string | null;
    vendorId: number | null;
    billingDates?: string[];
  } = {
    employeeId: '',
    employeeName: '',
    contractorName: '',
    jobTitle: '',
    location: null,
    vendorId: null,
  };
  @Input() selectedyear: number = 0;
  @Input() selectedmonth: number = 0;
  @Input() componentName: string = '';
  @Input() useEmployeeTimesheet: boolean = false;
  @Input() employeeTimesheetData: any | null = null;
  
  selectedDateValue!: Date;
  leaveDatePicker!: EmeraldDatepickerComponent;
  paidLeaveDates: string[] = [];
  unpaidLeaveDates: string[] = [];
  publicHolidayDates: string[] = [];
  month: number = 0;
  year: number = 0;
  summary: SummaryItem[] = [];
  currentCalendarMonth: number = 0;
  currentCalendarYear: number = 0;
  legendItems = LEGEND_ITEMS;
  calendarLegend = Attendance_Items;
  @Output() closeComponent = new EventEmitter<void>();
  @Output() refreshTimesheet = new EventEmitter<void>();
  hasSubmitted: boolean = false;
  submitted: boolean = true;
  isDropdownVisible: boolean = false;
  selectedLeaveType: string = '';
  isLeaveTypeSelected: boolean = false;
  errorMessage: string = '';
  leaveTypes = LEAVE_TYPE_LIST;
  selectedLeaveDates: { [date: string]: string } = {};
  private subscriptions: Subscription = new Subscription();
  buttonLabels = ButtonLabels;
  apiMarkedDates: MarkedDate[] = [];
  userMarkedDates: MarkedDate[] = [];
  private timesheetHelper: TimesheetCalendarHelper;
  opened: boolean = false;
  iconFontSize = icon_fontSize;
  newMessage: string = '';
  personaType = PersonaType;
  dateFormat = dateFormat;
  commentHistory: LeaveComment[] = [];
  private lastFetchKey: string = '';
  private isLoadingData: boolean = false;
  @ViewChild('modeChoiceRef') modeChoiceRef!: ElementRef;
  private lastPolledMonth: number = -1;
  private lastPolledYear: number = -1;
  private calendarPollInterval: any;
  contractorId: string = '';
  selectedRange: DateRange<Date> | null = null;
  selectedEmployeeTimesheet: any = { status: '' };
  firstClickDate: Date | null = null;
  pendingSecondDate: Date | null = null;
  showModeChoicePopup: boolean = false;
  modeChoicePosition: { top: number; left: number } = { top: 0, left: 0 };
  individualPendingDates: Date[] = [];
  selectedAction: string = '';
  hasUnselectPending: boolean = false;
  private _unselectHasLeave: boolean = false;
  private _unselectHasBilling: boolean = false;
  private _unselectBillingDates: string[] = [];
  private _unselectLeaveDates: string[] = [];
  localBillingDates: string[] | null = null;
  private _cachedBillingDateSet: Set<string> = new Set();
  private _cachedBillingKey: string = '';
  private _skipNextDocumentClick: boolean = false;

  constructor(
    private vendorService: VendorService,
    private authService: AuthService,
    private monthYearService: MonthYearService,
    private renderer: Renderer2,
    private timesheetCalendarService: TimesheetCalendarService,
    private toasterService: ToasterService,
    private billingHighlightService: BillingHighlightService,
  ) {
    this.timesheetHelper = new TimesheetCalendarHelper();
    const savedApiMarkedDates = localStorage.getItem('markedDates');
    if (savedApiMarkedDates) {
      this.markedDates = JSON.parse(savedApiMarkedDates);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    const isTimesheetComponent =
      this.componentName === PersonaType.Timesheet ||
      this.componentName === PersonaType.vendorRepTimesheet ||
      this.componentName === PersonaType.ContractorTimesheet;

    const hasDateChanged =
      'selectedyear' in changes || 'selectedmonth' in changes;

    if (isTimesheetComponent && (hasDateChanged || 'employeeTimesheetData' in changes)) {
      this.fetchLeaveSummary();
    }
  }

  ngOnInit(): void {
    const savedApiMarkedDates = localStorage.getItem('markedDates');
    if (savedApiMarkedDates) {
      this.apiMarkedDates = JSON.parse(savedApiMarkedDates);
    }
    if (!this.selectedyear || !this.selectedmonth) {
      const today = new Date();
      this.selectedyear = today.getFullYear();
      this.selectedmonth = today.getMonth() + 1;
    }
    this.selectedDateValue = new Date(
      this.selectedyear,
      this.selectedmonth - 1,
    );
    if (
      this.componentName == PersonaType.Timesheet ||
      this.componentName == PersonaType.vendorRepTimesheet ||
      this.componentName == PersonaType.ContractorTimesheet
    ) {
      this.monthYearService.setMonthAndYear(this.selectedmonth, this.selectedyear);
    }
    if (
      this.componentName == PersonaType.Timesheet ||
      this.componentName == PersonaType.vendorRepTimesheet ||
      this.componentName == PersonaType.ContractorTimesheet
    ) {
      this.subscriptions.add(
        this.monthYearService.month$.subscribe((month) => {
          this.month = month;
          this.fetchLeaveSummary();
        }),
      );

    this.subscriptions.add(
      this.monthYearService.year$.subscribe((year) => {
        this.year = year;
        if (this.componentName == PersonaType.Timesheet){
           this.selectedyear = year;
           this.selectedDateValue = new Date(this.selectedyear, this.selectedmonth - 1);
        }
       
        this.fetchLeaveSummary();
      }),
    );
    }

    if (this.componentName == PersonaType.RtoContractorList) {
      this.processLeaveSummary(this.candidateDetails);
    }
    this.updateLeaveTypeOptions();
  }

  ngAfterViewInit() {
    if (
      this.componentName == PersonaType.Timesheet ||
      this.componentName == PersonaType.ContractorTimesheet
    ) {
      this.calendarPollInterval = setInterval(() => {
        if (this.calendar && this.calendar.activeDate) {
          const month = this.calendar.activeDate.getMonth() + 1;
          const year = this.calendar.activeDate.getFullYear();
          if (month !== this.lastPolledMonth || year !== this.lastPolledYear) {
            this.lastPolledMonth = month;
            this.lastPolledYear = year;
            this.selectedmonth = month;
            this.selectedyear = year;
            this.fetchLeaveSummary();
          }
        }
      }, 300);
    }
    if (
      this.componentName == PersonaType.vendorRepTimesheet
    ) {
      this.calendarPollInterval = setInterval(() => {
        if (this.calendar && this.calendar.activeDate) {
          const month = this.calendar.activeDate.getMonth() + 1;
          const year = this.calendar.activeDate.getFullYear();
          if (month !== this.lastPolledMonth || year !== this.lastPolledYear) {
            this.lastPolledMonth = month;
            this.lastPolledYear = year;
            this.selectedmonth = month;
            this.selectedyear = year;
            this.fetchLeaveSummary();
          }
        }
      }, 300);
    }
  }

  ngOnDestroy(): void {
    if (this.calendarPollInterval) {
      clearInterval(this.calendarPollInterval);
    }
    this.subscriptions.unsubscribe();
  }

  markedDates: MarkedDate[] = [];
  selectedDates: Date[] = [];
  dropdownPosition = { top: 0, left: 0 };
  dateTypes = [
    { value: 'paid', label: 'Paid Leave' },
    { value: 'unpaid', label: 'Unpaid Leave' },
  ];
  dropdownDate: Date | null = null;
  dates: MarkedDate[] = [];
  selectedDate: Date | null = null;

  fetchLeaveSummary(): void {
    if (!this.isValidFetchRequest()) {
      return;
    }
    const currentFetchKey = `${this.candidateDetails.employeeId}-${this.candidateDetails.location}-${this.selectedmonth}-${this.selectedyear}`;
    if (this.lastFetchKey === currentFetchKey || this.isLoadingData) {
      return;
    }
    this.lastFetchKey = currentFetchKey;
    this.isLoadingData = true;
    this.localBillingDates = null;

    if (this.useEmployeeTimesheet && this.employeeTimesheetData) {
      const monthNumber = parseInt(this.employeeTimesheetData?.monthNumber, 10);
      const year = this.employeeTimesheetData?.year;
      if (monthNumber === this.selectedmonth && year === this.selectedyear) {
        const normalized = this.normalizeTimesheetData(this.employeeTimesheetData);
        this.processLeaveSummary(normalized);
        if (this.isCommentsEnabled()) {
          this.loadLeaveComments();
        }
        this.contractorId = normalized.employeeId || this.candidateDetails.employeeId;
        this.isLoadingData = false;
        if (this.componentName === PersonaType.ContractorTimesheet) {
          this.localBillingDates = normalized.billableDates ?? null;
          this._cachedBillingKey = '';
          this.selectedEmployeeTimesheet = { status: normalized.status ?? '' };
          this.refreshBillingHighlight();
        }
        if (
          this.componentName === PersonaType.Timesheet ||
          this.componentName === PersonaType.vendorRepTimesheet ||
          this.componentName === PersonaType.ContractorTimesheet
        ) {
          this.getTimesheetDetails(
            this.candidateDetails.employeeId,
            this.selectedmonth,
            this.selectedyear,
          );
        }
        return;
      }
    }

    this.vendorService
      .getLeaveSummary(
        this.candidateDetails.employeeId,
        this.candidateDetails.location || '',
        this.candidateDetails.vendorId || null,
        this.selectedmonth,
        this.selectedyear,
      )
      .subscribe(
        (data) => {
          this.processLeaveSummary(data);
          if (this.isCommentsEnabled()) {
            this.loadLeaveComments();
          }
          this.contractorId = data.employeeId;
          this.isLoadingData = false;

          if (
            this.componentName === PersonaType.Timesheet ||
            this.componentName === PersonaType.vendorRepTimesheet ||
            this.componentName === PersonaType.ContractorTimesheet
          ) {
            this.getTimesheetDetails(
              this.candidateDetails.employeeId,
              this.selectedmonth,
              this.selectedyear,
            );
          }
        },
        (error) => {
          this.handleFetchError(error);
          this.isLoadingData = false;
        },
      );
  }

  private isCommentsEnabled(): boolean {
    return (
      this.componentName === PersonaType.Timesheet ||
      this.componentName === PersonaType.vendorRepTimesheet ||
      this.componentName === PersonaType.ContractorTimesheet
    );
  }

  private normalizeTimesheetData(data: any): any {
    const toArray = (arr: any) => (Array.isArray(arr) ? arr : []);
    const halfPaidCount = toArray(data.halfPaidLeaveDates).length;
    const halfUnpaidCount = toArray(data.halfUnpaidLeaveDates).length;
    const computedTotal = (data?.paidLeaves || 0) + (data?.unpaidLeaves || 0) + 0.5 * (halfPaidCount + halfUnpaidCount);
    const totalLeaves = typeof data?.leavesAvailed === 'number' ? data.leavesAvailed : computedTotal;
    return {
      ...data,
      totalLeaves,
      paidLeaveDates: toArray(data.paidLeaveDates),
      unpaidLeaveDates: toArray(data.unpaidLeaveDates),
      halfPaidLeaveDates: toArray(data.halfPaidLeaveDates),
      halfUnpaidLeaveDates: toArray(data.halfUnpaidLeaveDates),
      publicHolidayDates: toArray(data.publicHolidayDates),
    };
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this._skipNextDocumentClick) {
      this._skipNextDocumentClick = false;
      return;
    }
    if (this.showModeChoicePopup) {
      const clickedInsideModeChoice = this.modeChoiceRef?.nativeElement?.contains(event.target);
      if (!clickedInsideModeChoice) {
        this.showModeChoicePopup = false;
        this.firstClickDate = null;
        this.pendingSecondDate = null;
        this.selectedRange = null;
        if (this.calendar) this.calendar.updateTodaysDate();
        this.refreshBillingHighlight();
      }
    }
  }

  private isValidFetchRequest(): boolean {
    if (!this.candidateDetails.employeeId) {
      this.errorMessage = ERROR_MESSAGES.EMPLOYEE_ID_MISSING;
      return false;
    }

    if (!this.selectedmonth || !this.selectedyear) {
      this.errorMessage = ERROR_MESSAGES.MONTH_YEAR_MISSING;
      return false;
    }

    return true;
  }

  private processLeaveDates(data: any): void {
    this.apiMarkedDates = this.timesheetHelper.processLeaveDates(data);
    this.dates = [...this.apiMarkedDates];
    localStorage.setItem('apiMarkedDates', JSON.stringify(this.apiMarkedDates));
    setTimeout(() => {
      if (this.calendar) {
        this.calendar.updateTodaysDate();
      }
      this.refreshCalendar();
    }, 0);
  }

  refreshCalendar(): void {
    setTimeout(() => {
      const calendarCells = document.querySelectorAll(
        '.mat-calendar-body-cell',
      );
      calendarCells.forEach((cell) => {
        const htmlCell = cell as HTMLElement;
        const ariaLabel = htmlCell.getAttribute('aria-label');
        if (!ariaLabel) return;
        const cellDate = new Date(ariaLabel);
        if (
          cellDate.getFullYear() !== this.selectedyear ||
          cellDate.getMonth() + 1 !== this.selectedmonth
        ) {
          return;
        }
        const dateStr = this.dateToString(cellDate);
        htmlCell.classList.remove(
          LeaveTypeEnum.Paid,
          LeaveTypeEnum.Unpaid,
          LeaveTypeEnum.PublicHoliday,
          LeaveTypeEnum.HalfPaid,
          LeaveTypeEnum.HalfUnpaid,
          'dual-half-leave',
          'dual-halfpaid',
          'dual-halfunpaid',
        );
        if (
          this.componentName === PersonaType.Timesheet ||
          this.componentName === PersonaType.vendorRepTimesheet ||
          this.componentName === PersonaType.ContractorTimesheet
        ) {
          const apiDate = this.apiMarkedDates.find((md) => md.date === dateStr);
          if (apiDate) {
            if (apiDate.type === LeaveTypeEnum.Paid)
              htmlCell.classList.add(LeaveTypeEnum.Paid);
            if (apiDate.type === LeaveTypeEnum.HalfPaid)
              htmlCell.classList.add(LeaveTypeEnum.HalfPaid);
            if (apiDate.type === LeaveTypeEnum.Unpaid)
              htmlCell.classList.add(LeaveTypeEnum.Unpaid);
            if (apiDate.type === LeaveTypeEnum.HalfUnpaid)
              htmlCell.classList.add(LeaveTypeEnum.HalfUnpaid);
            if (apiDate.type === LeaveTypeEnum.Public)
              htmlCell.classList.add(LeaveTypeEnum.PublicHoliday);
          }
          const userDate = this.userMarkedDates.find(
            (md) => md.date === dateStr,
          );
          if (userDate) {
            if (userDate.type === LeaveTypeEnum.Paid)
              htmlCell.classList.add(LeaveTypeEnum.Paid);
            if (userDate.type === LeaveTypeEnum.HalfPaid)
              htmlCell.classList.add(LeaveTypeEnum.HalfPaid);
            if (userDate.type === LeaveTypeEnum.Unpaid)
              htmlCell.classList.add(LeaveTypeEnum.Unpaid);
            if (userDate.type === LeaveTypeEnum.HalfUnpaid)
              htmlCell.classList.add(LeaveTypeEnum.HalfUnpaid);
          }
          if (this.getHalfLeaveCountForDate(dateStr) >= 2) {
            htmlCell.classList.remove(LeaveTypeEnum.HalfPaid, LeaveTypeEnum.HalfUnpaid);
            htmlCell.classList.add(this.getDualHalfLeaveClass(dateStr));
          }
        } else if (this.componentName === PersonaType.RtoContractorList) {
          const apiDate = this.apiMarkedDates.find((md) => md.date === dateStr);
          if (apiDate) {
            if (apiDate.type === rtoTypeEnum.Office_days)
              htmlCell.classList.add(rtoTypeEnum.Office_days);
            if (apiDate.type === rtoTypeEnum.Remote_days)
              htmlCell.classList.add(rtoTypeEnum.Remote_days);
            if (apiDate.type === rtoTypeEnum.Absent_days)
              htmlCell.classList.add(rtoTypeEnum.Absent_days);
            if (apiDate.type === rtoTypeEnum.Holiday_days)
              htmlCell.classList.add(rtoTypeEnum.Holiday_days);
          }
          const userDate = this.userMarkedDates.find(
            (md) => md.date === dateStr,
          );
          if (userDate) {
            if (userDate.type === rtoTypeEnum.Office_days)
              htmlCell.classList.add(rtoTypeEnum.Office_days);
            if (userDate.type === rtoTypeEnum.Remote_days)
              htmlCell.classList.add(rtoTypeEnum.Remote_days);
            if (userDate.type === rtoTypeEnum.Absent_days)
              htmlCell.classList.add(rtoTypeEnum.Absent_days);
            if (userDate.type === rtoTypeEnum.Holiday_days)
              htmlCell.classList.add(rtoTypeEnum.Holiday_days);
          }
        }
      });
      if (this.calendar) {
        this.calendar.updateTodaysDate();
      }
    }, 100);
  }

  private processLeaveSummary(data: any): void {
    this.processLeaveDates(data);
    this.summary = this.timesheetHelper.processLeaveSummary(
      data,
      SUMMARY_KEYS,
      SUMMARY_KEYS_ATTENDANCE,
      this.componentName,
    );
    this.updateLeaveTypeOptions();
    this.refreshBillingHighlight();
  }

  getFilteredSummary(): SummaryItem[] {
    if (this.componentName !== PersonaType.RtoContractorList) {
      const quarterlyLimit = this.summary.find(
        (item) => item.text === SUMMARY_KEYS.QUARTERLY_LIMIT,
      )?.value;

      if (quarterlyLimit === 0) {
        return this.summary.filter(
          (item) =>
            item.text !== SUMMARY_KEYS.TOTAL_LEAVES &&
            item.text !== SUMMARY_KEYS.PAID_LEAVES &&
            item.text !== SUMMARY_KEYS.QUARTERLY_LIMIT,
        );
      }
      return this.summary;
    }
    return this.summary.filter(
      (item) =>
        item.text === SUMMARY_KEYS_ATTENDANCE.OFFICE_DAYS ||
        item.text === SUMMARY_KEYS_ATTENDANCE.REMOTE_DAYS ||
        item.text === SUMMARY_KEYS_ATTENDANCE.ABSENT_DAYS ||
        item.text === SUMMARY_KEYS_ATTENDANCE.HOLIDAY_DAYS,
    );
  }

  getFilteredLegendItems(): CalendarLegendItem[] {
    const quarterlyLimit = this.summary.find(
      (item) => item.text === SUMMARY_KEYS.QUARTERLY_LIMIT,
    )?.value;
    const billingLegend: CalendarLegendItem = { label: 'Billable Hours', class: 'last-working-day' };

    if (this.componentName === PersonaType.Timesheet) {
      if (quarterlyLimit === 0) {
        return [
          ...this.legendItems.filter(
            (item) =>
              item.label !== LeaveType.Paid && item.label !== LeaveType.HalfPaid,
          ) as CalendarLegendItem[],
          billingLegend,
        ];
      }
      return [...(this.legendItems as CalendarLegendItem[]), billingLegend];
    } else if (
      this.componentName === PersonaType.vendorRepTimesheet ||
      this.componentName === PersonaType.ContractorTimesheet
    ) {
      if (quarterlyLimit === 0) {
        return [
          ...this.legendItems.filter(
            (item) =>
              item.label !== LeaveType.Paid && item.label !== LeaveType.HalfPaid,
          ) as CalendarLegendItem[],
          billingLegend,
        ];
      }
      return [...(this.legendItems as CalendarLegendItem[]), billingLegend];
    } else if (this.componentName === PersonaType.RtoContractorList) {
      return RTO_CALENDAR_LEGEND;
    }
    return this.legendItems as CalendarLegendItem[];
  }

  private handleFetchError(error: any): void {
    if (
      error &&
      (error.httpStatus === 'INTERNAL_SERVER_ERROR' || error.status === 500)
    ) {
      error.message = ErrorMessages.UnexpectedError;
      this.summary = [
        { text: SUMMARY_KEYS.TOTAL_LEAVES, value: 0 },
        { text: SUMMARY_KEYS.PAID_LEAVES, value: 0 },
        { text: SUMMARY_KEYS.UNPAID_LEAVES, value: 0 },
        { text: SUMMARY_KEYS.QUARTERLY_LIMIT, value: 0 },
        { text: SUMMARY_KEYS.QUARTERLY_AVAILED, value: 0 },
        { text: SUMMARY_KEYS.QUARTERLY_LIMIT, value: 0 },
      ];
      this.errorMessage = ERROR_MESSAGES.NO_DATA_AVAILABLE;
      return;
    }

    this.handleError(error, ERROR_MESSAGES.FETCH_LEAVE_SUMMARY_FAILED);

    this.summary = [
      { text: SUMMARY_KEYS.TOTAL_LEAVES, value: 0 },
      { text: SUMMARY_KEYS.PAID_LEAVES, value: 0 },
      { text: SUMMARY_KEYS.UNPAID_LEAVES, value: 0 },
      { text: SUMMARY_KEYS.QUARTERLY_LIMIT, value: 0 },
      { text: SUMMARY_KEYS.QUARTERLY_AVAILED, value: 0 },
      { text: SUMMARY_KEYS.QUARTERLY_LIMIT, value: 0 },
    ];
  }

  private handleError(error: any, message: string): void {
    alert(message);
    this.errorMessage = message;
  }

  close(): void {
    localStorage.removeItem('markedDates');
    this.selectedLeaveDates = {};
    this.closeComponent.emit();
  }

  formatDate(dateString: string | number | Date): string {
    return this.timesheetHelper.formatDate(dateString);
  }

  closeDropdown(): void {
    this.isDropdownVisible = false;
    document.body.classList.remove('dim-background');
  }

  leaveTypeOptions: { value: string; label: string }[] = [];

  private updateLeaveTypeOptions(): void {
    this.leaveTypeOptions = this.timesheetHelper.getLeaveTypes(this.summary, SUMMARY_KEYS);
  }

  getLeaveTypes(): { value: string; label: string }[] {
    return this.timesheetHelper.getLeaveTypes(this.summary, SUMMARY_KEYS);
  }

  cancel(): void {
    localStorage.removeItem('markedDates');
    this.selectedLeaveDates = {};
    this.selectedAction = '';
    this.firstClickDate = null;
    this.pendingSecondDate = null;
    this.individualPendingDates = [];
    this._clearUnselectState();
    this.selectedRange = null;
    this.showModeChoicePopup = false;
    this.errorMessage = '';
    if (this.calendar) this.calendar.updateTodaysDate();
    this.closeComponent.emit();
  }

  considerBilling(): void {
    const employeeId = this.candidateDetails.employeeId;
    const billingDates: string[] = [];

    const isPublicHoliday = (dateStr: string): boolean =>
      this.apiMarkedDates.some(md => md.date === dateStr && md.type === 'public');

    const isLeave = (dateStr: string): boolean =>
      this.apiMarkedDates.some(md => md.date === dateStr && md.type !== 'public') ||
      this.userMarkedDates.some(md => md.date === dateStr);

    if (this.individualPendingDates.length > 0) {
      this.individualPendingDates.forEach(d => {
        const day = d.getDay();
        if (day !== 0 && day !== 6) {
          const dateStr = this.dateToString(d);
          if (!isPublicHoliday(dateStr) && !isLeave(dateStr)) {
            billingDates.push(this.formatWorkingDate(d));
          }
        }
      });
    } else if (this.selectedRange?.start && this.selectedRange?.end) {
      const current = new Date(this.selectedRange.start);
      const end = this.selectedRange.end;
      while (current <= end) {
        const day = current.getDay();
        if (day !== 0 && day !== 6) {
          const dateStr = this.dateToString(current);
          if (!isPublicHoliday(dateStr) && !isLeave(dateStr)) {
            billingDates.push(this.formatWorkingDate(current));
          }
        }
        current.setDate(current.getDate() + 1);
      }
    }

    const monthLeaves = this.apiMarkedDates
      .filter(md => md.type !== 'public' && md.date.startsWith(`${this.selectedyear}-${String(this.selectedmonth).padStart(2, '0')}`))
      .map(md => {
        const [y, m, d] = md.date.split('-');
        return `${d}-${m}-${y}`;
      });

    if (billingDates.length === 0 && monthLeaves.length === 0) return;

    const existing = this.localBillingDates ?? this.candidateDetails?.billingDates ?? [];
    const merged = Array.from(new Set([...existing, ...billingDates, ...monthLeaves]));

    this.vendorService.recalculateEmployeeTimesheet(
      employeeId,
      merged,
      merged.length === 0 ? this.selectedmonth : undefined,
      merged.length === 0 ? this.selectedyear : undefined,
    ).subscribe({
      next: (response) => {
        this.toasterService.showSuccess('Billing Hours Calculated successfully', response?.status || 'Success');
        this.selectedRange = null;
        this.individualPendingDates = [];
        this.firstClickDate = null;
        this.selectedLeaveDates = {};
        this.userMarkedDates = [];
        this.refreshTimesheet.emit();
        this.closeComponent.emit();
      },
      error: () => {
        this.toasterService.showError('Failed to calculate billing hours', 'Error');
      }
    });
  }

  private formatWorkingDate(date: Date): string {
    const day   = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year  = date.getFullYear();
    return `${day}-${month}-${year}`;
  }

  submit(event?: Event): void {
    if (event) {
      event.preventDefault();
    }

    const quarterlyLimit =
      this.summary.find((item) => item.text === SUMMARY_KEYS.QUARTERLY_LIMIT)
        ?.value || 0;
    if (!this.validateSubmission()) {
      return;
    }
    if (quarterlyLimit === 0) {
      const leaveDetails = this.prepareUnpaidLeaveDetails();
      this.vendorService.createLeaveDetails(leaveDetails).subscribe(
        (response) => this.handleSaveSuccess(response),
        (error) => this.handleSaveError(error),
      );
      return;
    }
    const leaveDetails = this.prepareLeaveDetails();
    this.vendorService.createLeaveDetails(leaveDetails).subscribe(
      (response) => {
        this.handleSaveSuccess(response);
        if (this.selectedDate) {
          this.selectedLeaveDates[this.formatDate(this.selectedDate)] =
            this.selectedLeaveType;
        }
      },
      (error) => this.handleSaveError(error),
    );
  }

  private handleSaveSuccess(response: any): void {
    this.selectedLeaveDates = {};
    this.userMarkedDates = [];
    this.updateLeaveSummary();
  }

  private handleSaveError(error: any): void {
    this.handleError(error, ERROR_MESSAGES.SAVE_LEAVE_DETAILS_FAILED);
  }

  private validateSubmission(): boolean {
    if (!this.validateLeaveType() || !this.validateUserProfile()) {
      return false;
    }

    const quarterlyLimit = this.getSummaryValue(SUMMARY_KEYS.QUARTERLY_LIMIT);
    const quarterlyAvailed = this.getSummaryValue(
      SUMMARY_KEYS.QUARTERLY_AVAILED,
    );
    const newPaidLeaves = Object.values(this.selectedLeaveDates).reduce(
      (total, leaveType) => {
        if (leaveType === LeaveType.Paid) {
          return total + 1;
        } else if (leaveType === LeaveType.HalfPaid) {
          return total + 0.5;
        }
        return total;
      },
      0,
    );

    if (
      quarterlyLimit > 0 &&
      quarterlyAvailed + newPaidLeaves > quarterlyLimit
    ) {
      this.errorMessage = ERROR_MESSAGES.QUARTERLY_LIMIT_EXCEEDED;
      return false;
    }

    return true;
  }

  private getSummaryValue(key: string): number {
    return this.summary.find((item) => item.text === key)?.value || 0;
  }

  private validateLeaveType(): boolean {
    return true;
  }

  private validateUserProfile(): boolean {
    const currentUser = this.authService.userProfile;
    if (!currentUser || !currentUser.email) {
      this.errorMessage = ERROR_MESSAGES.USER_PROFILE_MISSING;
      return false;
    }
    return true;
  }

  private getVendorId(): string | null {
    let vendorId: string | null = null;
    this.vendorService.currentVendor$.subscribe((vendor) => {
      if (vendor) {
        vendorId = vendor.id;
      }
    });
    return vendorId;
  }

  private prepareLeaveDetails(): LeaveDetails {
    const currentUser = this.authService.userProfile;
    const selectedMonthPrefix = `${this.selectedyear}-${String(this.selectedmonth).padStart(2, '0')}-`;

    const leavesFromUserMarked = this.userMarkedDates
      .filter((md) => md.date.startsWith(selectedMonthPrefix))
      .map((md) => ({
        date: md.date,
        leaveType: md.label,
      }));

    const leavesFromApi = this.apiMarkedDates
      .filter(
        (md) => md.type !== 'public' && md.date.startsWith(selectedMonthPrefix),
      )
      .map((md) => ({
        date: md.date,
        leaveType: md.label,
      }));

    const allLeaves = [
      ...leavesFromUserMarked,
      ...leavesFromApi,
    ];

    const uniqueLeavesMap = new Map<string, { date: string; leaveType: string }>();
    const halfLeaveOccurrences = new Map<string, number>();
    allLeaves.forEach((leave) => {
      const isHalf = leave.leaveType === 'Half Paid Leave' || leave.leaveType === 'Half Unpaid Leave';
      const baseKey = `${leave.date}_${leave.leaveType}`;
      if (isHalf) {
        const count = (halfLeaveOccurrences.get(baseKey) || 0) + 1;
        halfLeaveOccurrences.set(baseKey, count);
        if (count <= 2) uniqueLeavesMap.set(`${baseKey}_${count}`, leave);
      } else {
        if (!uniqueLeavesMap.has(baseKey)) uniqueLeavesMap.set(baseKey, leave);
      }
    });
    const leaves = Array.from(uniqueLeavesMap.values());

    return {
      employeeId: this.candidateDetails.employeeId,
      employeeName: this.candidateDetails.employeeName,
      jobTitle: this.candidateDetails.jobTitle,
      location: this.candidateDetails.location,
      createdBy: currentUser?.email || null,
      updatedBy: currentUser?.email || null,
      vendorId: this.getVendorId(),
      leaves: leaves,
      month: this.selectedmonth,
      year: this.selectedyear
    };
  }

  private prepareUnpaidLeaveDetails(): LeaveDetails {
    const currentUser = this.authService.userProfile;
    const selectedMonthPrefix = `${this.selectedyear}-${String(this.selectedmonth).padStart(2, '0')}-`;

    const leavesFromUserMarked = this.userMarkedDates
      .filter((md) => md.date.startsWith(selectedMonthPrefix))
      .map((md) => ({
        date: md.date,
        leaveType: LeaveType.Unpaid,
      }));

    const leavesFromApi = this.apiMarkedDates
      .filter(
        (md) => md.type !== 'public' && md.date.startsWith(selectedMonthPrefix),
      )
      .map((md) => ({
        date: md.date,
        leaveType: LeaveType.Unpaid,
      }));

    const allLeaves = [
      ...leavesFromUserMarked,
      ...leavesFromApi,
    ];

    const uniqueLeavesMap = new Map<string, { date: string; leaveType: string }>();
    const halfLeaveOccurrences = new Map<string, number>();
    allLeaves.forEach((leave) => {
      const isHalf = leave.leaveType === 'Half Paid Leave' || leave.leaveType === 'Half Unpaid Leave';
      const baseKey = `${leave.date}_${leave.leaveType}`;
      if (isHalf) {
        const count = (halfLeaveOccurrences.get(baseKey) || 0) + 1;
        halfLeaveOccurrences.set(baseKey, count);
        if (count <= 2) uniqueLeavesMap.set(`${baseKey}_${count}`, leave);
      } else {
        if (!uniqueLeavesMap.has(baseKey)) uniqueLeavesMap.set(baseKey, leave);
      }
    });
    const uniqueLeaves = Array.from(uniqueLeavesMap.values());

    const leaves = uniqueLeaves.length
      ? uniqueLeaves
      : this.selectedDate
        ? [
            {
              date: this.formatDate(this.selectedDate),
              leaveType: LeaveType.Unpaid,
            },
          ]
        : [];

    return {
      employeeId: this.candidateDetails.employeeId,
      employeeName: this.candidateDetails.employeeName,
      jobTitle: this.candidateDetails.jobTitle,
      location: this.candidateDetails.location,
      createdBy: currentUser?.email || null,
      updatedBy: currentUser?.email || null,
      vendorId: this.getVendorId(),
      leaves: leaves,
      month: this.selectedmonth,
      year: this.selectedyear
    };
  }

  private updateLeaveSummary(): void {
    this.fetchLeaveSummary();
    this.refreshTimesheet.emit();
    this.closeComponent.emit();
  }

private getBillingDateSet(): Set<string> {
  const billingDates = this.localBillingDates ?? this.candidateDetails?.billingDates ?? undefined;
  const key = `${this.selectedmonth}-${this.selectedyear}-${JSON.stringify(billingDates)}-${JSON.stringify(this._unselectBillingDates)}`;
  if (key !== this._cachedBillingKey) {
    this._cachedBillingKey = key;
    this._cachedBillingDateSet = this.billingHighlightService.getBillingDateSet(billingDates);
  }
  return this._cachedBillingDateSet;
}

  private getEffectiveBillingDateSet(): Set<string> {
    const full = this.getBillingDateSet();
    if (this._unselectBillingDates.length === 0) return full;
    const effective = new Set(full);
    this._unselectBillingDates.forEach(d => effective.delete(d));
    return effective;
  }

  get hasBillingDatesForCurrentMonth(): boolean {
    return this.getBillingDateSet().size > 0;
  }

  private refreshBillingHighlight(): void {
    if (
      this.componentName === PersonaType.vendorRepTimesheet ||
      this.componentName === PersonaType.ContractorTimesheet
    ) {
      if (this.calendar) this.calendar.updateTodaysDate();
    }
  }

  private getRestingClass(dateStr: string): string {
    if (this.getHalfLeaveCountForDate(dateStr) >= 2) return this.getDualHalfLeaveClass(dateStr);
    const user = this.userMarkedDates.find(md => md.date === dateStr);
    if (user) {
      if (user.type === 'paid')       return 'paid';
      if (user.type === 'unpaid')     return 'unpaid';
      if (user.type === 'halfpaid')   return 'halfpaid';
      if (user.type === 'halfunpaid') return 'halfunpaid';
    }
    const api = this.apiMarkedDates.find(md => md.date === dateStr);
    if (api) {
      if (api.type === 'paid')       return 'paid';
      if (api.type === 'unpaid')     return 'unpaid';
      if (api.type === 'halfpaid')   return 'halfpaid';
      if (api.type === 'halfunpaid') return 'halfunpaid';
      if (api.type === 'public')     return 'public-holiday';
    }
    const billing = this.getEffectiveBillingDateSet();
    if (billing.has(dateStr)) return 'last-working-day';
    return '';
  }

  private resolveClass(dateStr: string): string {
    const isPublicHol = this.apiMarkedDates.some(
      md => md.date === dateStr && md.type === 'public'
    );

    if (this.selectedAction === 'billing') {
      if (isPublicHol) return 'public-holiday';
      const existingLeave = this.userMarkedDates.find(md => md.date === dateStr) ||
        this.apiMarkedDates.find(md => md.date === dateStr && md.type !== 'public');
      if (existingLeave) return this.getRestingClass(dateStr);
      return 'last-working-day';
    }

    if (isPublicHol) return 'public-holiday';
    switch (this.selectedAction) {
      case 'paid':       return 'paid';
      case 'unpaid':     return 'unpaid';
      case 'halfpaid':   return 'halfpaid';
      case 'halfunpaid': return 'halfunpaid';
      default:           return 'selected-range-start-end';
    }
  }

  get effectiveSelection(): DateRange<Date> | null {
    return this.selectedRange;
  }

  private isInSelection(d: Date, dateStr: string): boolean {
    if (
      this.componentName !== PersonaType.vendorRepTimesheet &&
      this.componentName !== PersonaType.ContractorTimesheet
    ) return false;
    if (d.getDay() === 0 || d.getDay() === 6) return false;

    if (this.individualPendingDates.some(pd => this.dateToString(pd) === dateStr)) return true;
    if (this.pendingSecondDate && this.dateToString(this.pendingSecondDate) === dateStr) return true;

    if (this.selectedRange?.start) {
      if (!this.selectedRange.end) {
        return d.getTime() === this.selectedRange.start.getTime();
      }
      return d >= this.selectedRange.start && d <= this.selectedRange.end;
    }
    return false;
  }

  Today = [{ date: this.dateToString(new Date()), text: 'Today' }];
  dateClass = (d: Date): string => {
    const dateStr = this.dateToString(d);

    // Dual half-leave takes precedence over everything — check before billing
    if (this.getHalfLeaveCountForDate(dateStr) >= 2) return this.getDualHalfLeaveClass(dateStr);

    if (
      this.componentName === PersonaType.vendorRepTimesheet ||
      this.componentName === PersonaType.ContractorTimesheet
    ) {
      const dateSet = this.getEffectiveBillingDateSet();
      if (!this.isInSelection(d, dateStr)) {
        const billingClass = this.billingHighlightService.getBillingDateClass(
          d, dateSet, false, this.apiMarkedDates,
          (dt) => this.dateToString(dt),
        );
        if (billingClass) return billingClass;
      }
    }

    if (this.isInSelection(d, dateStr)) {
      return this.resolveClass(dateStr);
    }

    return this.getRestingClass(dateStr);
  };

  showModeChoiceForDate(date: Date): void {
    const calendarWrapper = document.querySelector('.calendar-wrapper');
    const cells = document.querySelectorAll('.mat-calendar-body-cell');
    let positioned = false;
    const popupWidth = 160;
    const popupHeight = 80;

    cells.forEach((cell) => {
      const htmlCell = cell as HTMLElement;
      const ariaLabel = htmlCell.getAttribute('aria-label');
      if (!ariaLabel) return;
      const cellDate = new Date(ariaLabel);
      if (cellDate.toDateString() === date.toDateString()) {
        const cellRect = htmlCell.getBoundingClientRect();
        const wrapperRect = calendarWrapper
          ? calendarWrapper.getBoundingClientRect()
          : { top: 0, left: 0, bottom: 9999, right: 9999, width: 400 };

        let top = cellRect.bottom - wrapperRect.top + 4;
        if (cellRect.bottom + popupHeight > wrapperRect.bottom) {
          top = cellRect.top - wrapperRect.top - popupHeight - 4;
        }
        top = Math.max(4, top);

        const calendarWidth = wrapperRect.width * 0.6;
        let left = cellRect.left - wrapperRect.left + cellRect.width / 2 - popupWidth / 2;
        left = Math.max(4, Math.min(left, calendarWidth - popupWidth - 4));

        this.modeChoicePosition = { top, left };
        positioned = true;
      }
    });

    if (!positioned) {
      this.modeChoicePosition = { top: 200, left: 40 };
    }
    this._skipNextDocumentClick = true;
    this.showModeChoicePopup = true;
  }

  chooseRangeMode(): void {
    if (!this.firstClickDate || !this.pendingSecondDate) return;
    this.showModeChoicePopup = false;
    const d1 = this.firstClickDate;
    const d2 = this.pendingSecondDate;
    this.firstClickDate = null;
    this.pendingSecondDate = null;
    if (d2 < d1) {
      this.selectedRange = new DateRange<Date>(d2, d1);
    } else {
      this.selectedRange = new DateRange<Date>(d1, d2);
    }
    this.calendar.updateTodaysDate();
    this.refreshBillingHighlight();
  }

  chooseIndividualMode(): void {
    if (!this.firstClickDate || !this.pendingSecondDate) return;
    this.showModeChoicePopup = false;
    this.individualPendingDates = [this.firstClickDate, this.pendingSecondDate];
    this.firstClickDate = null;
    this.pendingSecondDate = null;
    this.selectedRange = new DateRange<Date>(this.individualPendingDates[this.individualPendingDates.length - 1], this.individualPendingDates[this.individualPendingDates.length - 1]);
    this.calendar.updateTodaysDate();
    this.refreshBillingHighlight();
  }

  private _clearUnselectState(): void {
    this.hasUnselectPending = false;
    this._unselectHasLeave = false;
    this._unselectHasBilling = false;
    this._unselectBillingDates = [];
    this._unselectLeaveDates = [];
    this.selectedLeaveDates = {};
    this.userMarkedDates = [];
  }

  private getHalfLeaveCountForDate(dateStr: string): number {
    const apiCount = this.apiMarkedDates.filter(
      (md) => md.date === dateStr && (md.type === 'halfpaid' || md.type === 'halfunpaid')
    ).length;
    const userCount = this.userMarkedDates.filter(
      (md) => md.date === dateStr && (md.type === 'halfpaid' || md.type === 'halfunpaid')
    ).length;
    return apiCount + userCount;
  }

  private getDualHalfLeaveClass(dateStr: string): string {
    const hasPaid =
      this.apiMarkedDates.some((md) => md.date === dateStr && md.type === 'halfpaid') ||
      this.userMarkedDates.some((md) => md.date === dateStr && md.type === 'halfpaid');
    const hasUnpaid =
      this.apiMarkedDates.some((md) => md.date === dateStr && md.type === 'halfunpaid') ||
      this.userMarkedDates.some((md) => md.date === dateStr && md.type === 'halfunpaid');
    if (hasPaid && hasUnpaid) return 'dual-half-leave';
    if (hasPaid) return 'dual-halfpaid';
    return 'dual-halfunpaid';
  }

  onActionChange(): void {
    this.firstClickDate = null;
    this.pendingSecondDate = null;
    this.individualPendingDates = [];
    this._clearUnselectState();
    this.selectedRange = null;
    this.showModeChoicePopup = false;
    this.errorMessage = '';
    if (this.calendar) this.calendar.updateTodaysDate();
    this.refreshBillingHighlight();
  }

  hasSelectedDates(): boolean {
    return this.individualPendingDates.length > 0 ||
      (!!this.selectedRange?.start && !!this.selectedRange?.end) ||
      this.hasUnselectPending;
  }

  applyAction(event?: Event): void {
    if (!this.selectedAction) {
      this.errorMessage = 'Please select an action from the dropdown first.';
      return;
    }
    if (!this.hasSelectedDates()) {
      this.errorMessage = 'Please select at least one date.';
      return;
    }
    if (this.selectedAction === 'billing') {
      this.considerBilling();
    } else if (this.selectedAction === 'unselect') {

      if (this._unselectHasBilling && this._unselectHasLeave) {
        if (!this.validateSubmission()) return;
        const leaveDetails = this.prepareLeaveDetails();
        this.vendorService.createLeaveDetails(leaveDetails).subscribe({
          next: (leaveResponse) => {
            const fullSet = this.getBillingDateSet();
            const removedSet = new Set(this._unselectBillingDates);
            const remaining = Array.from(fullSet)
              .filter(d => !removedSet.has(d))
              .map(d => {
                const [y, m, day] = d.split('-').map(Number);
                return this.formatWorkingDate(new Date(y, m - 1, day));
              });
            this.vendorService.recalculateEmployeeTimesheet(
              this.candidateDetails.employeeId,
              remaining,
              remaining.length === 0 ? this.selectedmonth : undefined,
              remaining.length === 0 ? this.selectedyear : undefined,
            ).subscribe({
              next: (billingResponse) => {
                this.toasterService.showSuccess(
                  'Billing Hours Calculated successfully',
                  billingResponse?.status || 'Success'
                );
                this._clearUnselectState();
                this.refreshTimesheet.emit();
                this.closeComponent.emit();
              },
              error: () => this.toasterService.showError('Failed to calculate billing hours', 'Error'),
            });
          },
          error: (error) => this.handleSaveError(error),
        });
      } else if (this._unselectHasBilling) {
        const fullSet = this.getBillingDateSet();
        const removedSet = new Set(this._unselectBillingDates);
        const remaining = Array.from(fullSet)
          .filter(d => !removedSet.has(d))
          .map(d => {
            const [y, m, day] = d.split('-').map(Number);
            return this.formatWorkingDate(new Date(y, m - 1, day));
          });
        this.vendorService.recalculateEmployeeTimesheet(
          this.candidateDetails.employeeId,
          remaining,
          remaining.length === 0 ? this.selectedmonth : undefined,
          remaining.length === 0 ? this.selectedyear : undefined,
        ).subscribe({
          next: (response) => {
            this.toasterService.showSuccess(
              'Billing Hours Calculated successfully',
              response?.status || 'Success'
            );
            this._clearUnselectState();
            this.refreshTimesheet.emit();
            this.closeComponent.emit();
          },
          error: () => this.toasterService.showError('Failed to calculate billing hours', 'Error'),
        });
      } else if (this._unselectHasLeave) {
        const existingBilling = this.localBillingDates ?? this.candidateDetails?.billingDates ?? [];

        if (existingBilling.length > 0) {
          if (!this.validateSubmission()) return;
          const leaveDetails = this.prepareLeaveDetails();
          this.vendorService.createLeaveDetails(leaveDetails).subscribe({
            next: (leaveResponse) => {
              const reAddDates = this._unselectLeaveDates.map(d => {
                const [y, m, day] = d.split('-');
                return `${day}-${m}-${y}`;
              });
              const merged = Array.from(new Set([...existingBilling, ...reAddDates]));

              this.vendorService.recalculateEmployeeTimesheet(
                this.candidateDetails.employeeId,
                merged,
                merged.length === 0 ? this.selectedmonth : undefined,
                merged.length === 0 ? this.selectedyear : undefined,
              ).subscribe({
                next: (billingResponse) => {
                  this.toasterService.showSuccess(
                    'Billing Hours Calculated successfully',
                    billingResponse?.status || 'Success'
                  );
                  this._clearUnselectState();
                  this.refreshTimesheet.emit();
                  this.closeComponent.emit();
                },
                error: () => this.toasterService.showError('Failed to calculate billing hours', 'Error'),
              });
            },
            error: (error) => this.handleSaveError(error),
          });
        } else {
          this.submit(event);
          this._clearUnselectState();
        }
      }
    } else {
  // Collect all valid leave dates BEFORE setDateType clears the selection
  const selectedLeaveDatesList: string[] = [];
  const isHolidayDate = (s: string) =>
    this.apiMarkedDates.some(md => md.date === s && md.type === 'public');

  if (this.individualPendingDates.length > 0) {
    this.individualPendingDates.forEach(d => {
      const day = d.getDay();
      if (day !== 0 && day !== 6) {
        const s = this.dateToString(d);
        if (!isHolidayDate(s)) selectedLeaveDatesList.push(s);
      }
    });
  } else if (this.selectedRange?.start && this.selectedRange?.end) {
    const cur = new Date(this.selectedRange.start);
    while (cur <= this.selectedRange.end!) {
      const day = cur.getDay();
      if (day !== 0 && day !== 6) {
        const s = this.dateToString(cur);
        if (!isHolidayDate(s)) selectedLeaveDatesList.push(s);
      }
      cur.setDate(cur.getDate() + 1);
    }
  }

  // Mark dates locally
  this.setDateType(this.selectedAction, event);

  if (this.userMarkedDates.length === 0 && Object.keys(this.selectedLeaveDates).length === 0) {
    this.errorMessage = 'Selected dates contain only public holidays or weekends. Please select valid working days.';
    this.selectedAction = '';
    return;
  }

  const existing = this.localBillingDates ?? this.candidateDetails?.billingDates ?? [];

  if (existing.length > 0) {
    // Billing array is non-empty — call leaves API then billing API,
    // merging the leave dates INTO the billing array regardless of prior billing status.
    if (!this.validateSubmission()) return;
    const leaveDetails = this.prepareLeaveDetails();
    this.vendorService.createLeaveDetails(leaveDetails).subscribe({
      next: (leaveResponse) => {
        // Convert YYYY-MM-DD → DD-MM-YYYY for the billing API
        const leaveDatesFormatted = selectedLeaveDatesList.map(d => {
          const [y, m, day] = d.split('-');
          return `${day}-${m}-${y}`;
        });
        const merged = Array.from(new Set([...existing, ...leaveDatesFormatted]));

        this.vendorService.recalculateEmployeeTimesheet(
          this.candidateDetails.employeeId,
          merged,
          merged.length === 0 ? this.selectedmonth : undefined,
          merged.length === 0 ? this.selectedyear : undefined,
        ).subscribe({
          next: (billingResponse) => {
            this.toasterService.showSuccess(
              'Billing Hours Calculated successfully',
              billingResponse?.status || 'Success'
            );
            this.selectedLeaveDates = {};
            this.userMarkedDates = [];
            this.refreshTimesheet.emit();
            this.closeComponent.emit();
          },
          error: () => this.toasterService.showError('Failed to calculate billing hours', 'Error'),
        });
      },
      error: (error) => this.handleSaveError(error),
    });
  } else {
    // No billing array — leaves only, normal submit
    this.submit(event);
  }
}
    this.selectedAction = '';
  }

  onDateSelected(event: Date | null): void {
    this.errorMessage = '';

    if (
      this.selectedEmployeeTimesheet?.status === 'Approved' ||
      this.selectedEmployeeTimesheet?.status === 'Pending Approval'
    ) {
      this.errorMessage = `Cannot modify timesheet when status is ${this.selectedEmployeeTimesheet.status}`;
      return;
    }

    if (!this.selectedAction) {
      this.errorMessage = 'Please select an action from the dropdown first.';
      return;
    }

    if (!event) {
      this.selectedRange = null;
      this.calendar.updateTodaysDate();
      return;
    }

    const day = event.getDay();
    if (day === 0 || day === 6) {
      this.errorMessage = ERROR_MESSAGES.WEEKEND_LEAVE_NOT_ALLOWED;
      this.selectedRange = null;
      this.calendar.updateTodaysDate();
      return;
    }

    const dateStr = this.dateToString(event);

    const isPublicHoliday = this.apiMarkedDates.some(
      (md) => md.date === dateStr && md.type === 'public',
    );
    if (isPublicHoliday) {
      this.selectedRange = null;
      this.calendar.updateTodaysDate();
      return;
    }

    const existingApiLeave = this.apiMarkedDates.find(
      (md) => md.date === dateStr && md.type !== 'public',
    );
    const isNewHalfAction = this.selectedAction === 'halfpaid' || this.selectedAction === 'halfunpaid';
    const existingHasHalf =
      existingApiLeave?.type === 'halfpaid' || existingApiLeave?.type === 'halfunpaid' ||
      this.userMarkedDates.some((md) => md.date === dateStr && (md.type === 'halfpaid' || md.type === 'halfunpaid'));
    const isComplementaryHalfDay = existingHasHalf && isNewHalfAction;
    if (isComplementaryHalfDay && this.getHalfLeaveCountForDate(dateStr) >= 2) {
      this.errorMessage = 'Maximum of 2 half-leaves can be applied per day.';
      return;
    }
    if (
      existingApiLeave &&
      !isComplementaryHalfDay &&
      this.selectedAction !== 'unselect' &&
      this.selectedAction !== 'billing' &&
      (this.componentName === PersonaType.vendorRepTimesheet ||
       this.componentName === PersonaType.ContractorTimesheet)
    ) {
      this.errorMessage = 'Leave type cannot be changed.';
      return;
    }

    // Unselect mode: individual dates only, no range
    if (this.selectedAction === 'unselect') {
      const isBillingDate = this.getBillingDateSet().has(dateStr);
      const isLeaveDate   = this.apiMarkedDates.some(md => md.date === dateStr) ||
                            this.userMarkedDates.some(md => md.date === dateStr);

      const isExistingApiLeave = this.apiMarkedDates.some(
        (md) => md.date === dateStr && md.type !== 'public',
      );
      if (isExistingApiLeave) {
        this.errorMessage = 'Existing leave dates cannot be modified or unselected.';
        return;
      }
      if (this.componentName === PersonaType.ContractorTimesheet && !isBillingDate) {
        this.errorMessage = 'Only billable dates can be unselected.';
        return;
      }

      this.errorMessage = '';
      this.userMarkedDates = this.userMarkedDates.filter(md => md.date !== dateStr);
      this.apiMarkedDates  = this.apiMarkedDates.filter(md => md.date !== dateStr);
      delete this.selectedLeaveDates[dateStr];

      if (isBillingDate) {
        this._unselectHasBilling = true;
        if (!this._unselectBillingDates.includes(dateStr)) this._unselectBillingDates.push(dateStr);
      }
      if (isLeaveDate) {
        this._unselectHasLeave = true;
        if (!this._unselectLeaveDates.includes(dateStr)) this._unselectLeaveDates.push(dateStr);
      }

      this.hasUnselectPending = true;
      this.selectedRange = null;
      this.calendar.updateTodaysDate();
      this.refreshBillingHighlight();
      this.refreshCalendar();
      return;
    }

    // If already accumulating individual dates, add this one
    if (this.individualPendingDates.length > 0) {
      if (!this.individualPendingDates.some(pd => this.dateToString(pd) === dateStr)) {
        this.individualPendingDates.push(event);
      }
      this.selectedRange = new DateRange<Date>(event, event);
      this.calendar.updateTodaysDate();
      this.refreshBillingHighlight();
      return;
    }

    if (!this.firstClickDate) {
      this.firstClickDate = event;
      this.selectedRange = new DateRange<Date>(event, event);
    } else {
      this.pendingSecondDate = event;
      this.showModeChoiceForDate(event);
    }
    this.calendar.updateTodaysDate();
    this.refreshBillingHighlight();
  }

  unselectDate(dateStr: string): void {
    this.userMarkedDates = this.userMarkedDates.filter(
      (md) => md.date !== dateStr,
    );
    delete this.selectedLeaveDates[dateStr];
    localStorage.setItem('markedDates', JSON.stringify(this.userMarkedDates));
  }

  setDateType(type: string | null, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }

    if (
      !this.selectedRange ||
      !this.selectedRange.start ||
      !this.selectedRange.end
    ) {
      this.errorMessage = ERROR_MESSAGES.WEEKEND_LEAVE_PROHIBITED;
      return;
    }

    const start = this.selectedRange.start;
    const end = this.selectedRange.end;
    let currentDate = new Date(start);

    if (type === null || type === '') {
      const isSingleDateSelection = start.getTime() === end.getTime();

      while (currentDate <= end) {
        const dayOfWeek = currentDate.getDay();
        const dateStr = this.dateToString(currentDate);

        if (dayOfWeek === 0 || dayOfWeek === 6) {
          currentDate.setDate(currentDate.getDate() + 1);
          continue;
        }

        this.userMarkedDates = this.userMarkedDates.filter(
          (md) => md.date !== dateStr,
        );
        delete this.selectedLeaveDates[dateStr];

        if (isSingleDateSelection) {
          this.apiMarkedDates = this.apiMarkedDates.filter(
            (md) => md.date !== dateStr,
          );
        }

        currentDate.setDate(currentDate.getDate() + 1);
      }

      this.individualPendingDates.forEach(pd => {
        const pdStr = this.dateToString(pd);
        this.userMarkedDates = this.userMarkedDates.filter(md => md.date !== pdStr);
        delete this.selectedLeaveDates[pdStr];
      });

      this.selectedRange = null;
      this.calendar.updateTodaysDate();

      this.individualPendingDates = [];
      this.firstClickDate = null;
      this.pendingSecondDate = null;

      this.refreshCalendar();
      return;
    }

    let label: string;
    const typeLabelMap: { [key: string]: string } = {
      paid: LeaveType.Paid,
      halfpaid: LeaveType.HalfPaid,
      unpaid: LeaveType.Unpaid,
      halfunpaid: LeaveType.HalfUnpaid,
    };
    label = typeLabelMap[type] ?? LeaveType.HalfUnpaid;

    while (currentDate <= end) {
      const dayOfWeek = currentDate.getDay();
      const dateStr = this.dateToString(currentDate);

      if (dayOfWeek === 0 || dayOfWeek === 6) {
        currentDate.setDate(currentDate.getDate() + 1);
        continue;
      }

      const isHoliday = this.apiMarkedDates.some(
        md => md.date === dateStr && md.type === 'public'
      );
      if (isHoliday) {
        currentDate.setDate(currentDate.getDate() + 1);
        continue;
      }

      const isNewHalf = type === 'halfpaid' || type === 'halfunpaid';
      const existingApiLeaveForDate = this.apiMarkedDates.find(
        (md) => md.date === dateStr && md.type !== 'public',
      );
      const existingHasHalfForDate =
        existingApiLeaveForDate?.type === 'halfpaid' || existingApiLeaveForDate?.type === 'halfunpaid' ||
        this.userMarkedDates.some((md) => md.date === dateStr && (md.type === 'halfpaid' || md.type === 'halfunpaid'));
      const isComplementaryHalfDay = existingHasHalfForDate && isNewHalf;
      if (existingApiLeaveForDate && !isComplementaryHalfDay) {
        this.errorMessage = 'Leave type cannot be changed.';
      } else if (isComplementaryHalfDay && this.getHalfLeaveCountForDate(dateStr) >= 2) {
        this.errorMessage = 'Maximum of 2 half-leaves can be applied per day.';
      } else {
        if (!isComplementaryHalfDay) {
          this.apiMarkedDates = this.apiMarkedDates.filter((md) => md.date !== dateStr);
          this.userMarkedDates = this.userMarkedDates.filter((md) => md.date !== dateStr);
        }
        this.userMarkedDates.push({ date: dateStr, type, label });
        this.selectedLeaveDates[dateStr] = label;
      }

      currentDate.setDate(currentDate.getDate() + 1);
    }

    this.selectedRange = null;
    this.calendar.updateTodaysDate();

    this.individualPendingDates.forEach(pd => {
      const pdStr = this.dateToString(pd);
      const day = pd.getDay();
      if (day === 0 || day === 6) return;
      const isHoliday = this.apiMarkedDates.some(
        md => md.date === pdStr && md.type === 'public'
      );
      if (isHoliday) return;
      const isNewHalfForPd = type === 'halfpaid' || type === 'halfunpaid';
      const existingApiLeaveForPd = this.apiMarkedDates.find(
        (md) => md.date === pdStr && md.type !== 'public',
      );
      const existingHasHalfForPd =
        existingApiLeaveForPd?.type === 'halfpaid' || existingApiLeaveForPd?.type === 'halfunpaid' ||
        this.userMarkedDates.some((md) => md.date === pdStr && (md.type === 'halfpaid' || md.type === 'halfunpaid'));
      const isComplementaryHalfDayForPd = existingHasHalfForPd && isNewHalfForPd;
      if (existingApiLeaveForPd && !isComplementaryHalfDayForPd) {
        this.errorMessage = 'Leave type cannot be changed.';
      } else if (isComplementaryHalfDayForPd && this.getHalfLeaveCountForDate(pdStr) >= 2) {
        this.errorMessage = 'Maximum of 2 half-leaves can be applied per day.';
      } else {
        if (!isComplementaryHalfDayForPd) {
          this.apiMarkedDates = this.apiMarkedDates.filter(md => md.date !== pdStr);
          this.userMarkedDates = this.userMarkedDates.filter(md => md.date !== pdStr);
        }
        this.userMarkedDates.push({ date: pdStr, type, label });
        this.selectedLeaveDates[pdStr] = label;
      }
    });
    this.individualPendingDates = [];
    this.firstClickDate = null;
    this.pendingSecondDate = null;

    this.refreshCalendar();
  }

  dateToString(date: Date): string {
    return (
      date.getFullYear() +
      '-' +
      String(date.getMonth() + 1).padStart(2, '0') +
      '-' +
      String(date.getDate()).padStart(2, '0')
    );
  }

  getCurrentCalendarDateForComments(): string {
    const month =
      this.currentCalendarMonth ||
      this.selectedmonth ||
      new Date().getMonth() + 1;
    const year =
      this.currentCalendarYear || this.selectedyear || new Date().getFullYear();
    const utcDate = new Date(Date.UTC(year, month - 1, 1));
    utcDate.setMinutes(utcDate.getMinutes() + 330);
    return utcDate.toISOString().split('T')[0];
  }

  displayMonth(): void {
    this.timesheetHelper.displayMonth(
      this.dates,
      this.Today,
      this.handleError.bind(this),
    );
  }

  streamOpened(): void {
    this.timesheetHelper.streamOpened(
      this.renderer,
      this.displayMonth.bind(this),
    );
  }

  private loadLeaveComments() {
    this.timesheetCalendarService
      .getLeaveComments(
        this.candidateDetails.employeeId,
        this.selectedmonth,
        this.selectedyear,
      )
      .subscribe({
        next: (response: LeaveCommentsResponse) => {
          this.commentHistory = response?.data || [];
        },
        error: (error) => {
          this.toasterService.showError(
            TIMESHEET_CONSTANTS.MESSAGES.FAILED_LOAD_COMMENTS,
            TIMESHEET_CONSTANTS.TITLES.LOAD_ERROR,
          );
        },
      });
  }

  sendMessage() {
    const message = this.newMessage.trim();
    if (!message) {
      return;
    }

    const currentUser = this.authService.userProfile;
    const currentDate = new Date().toISOString();
    const CalendercurrentDate = this.getCurrentCalendarDateForComments();
    const employeeId =
      this.componentName === PersonaType.Timesheet
        ? this.candidateDetails.employeeId
        : this.contractorId;

    type CommentData = {
      employeeId: string;
      commentedManagerName: string;
      managerComments: string;
      managerCommentsDate: string;
      commentedVendorName: string;
      vendorComments: string;
      vendorCommentsDate: string;
      commentsDate: string;
    };

    const commentData: CommentData = {
      employeeId: employeeId,
      commentedManagerName:
        this.componentName === PersonaType.Timesheet
          ? (currentUser?.firstName ?? '')
          : '',
      managerComments:
        this.componentName === PersonaType.Timesheet ? message : '',
      managerCommentsDate:
        this.componentName === PersonaType.Timesheet ? currentDate : '',
      commentedVendorName:
        this.componentName === PersonaType.Timesheet
          ? ''
          : (currentUser?.firstName ?? ''),
      vendorComments:
        this.componentName === PersonaType.Timesheet ? '' : message,
      vendorCommentsDate:
        this.componentName === PersonaType.Timesheet ? '' : currentDate,
      commentsDate: CalendercurrentDate,
    };

    this.timesheetCalendarService.saveComment(commentData).subscribe({
      next: () => {
        this.commentHistory?.push(commentData);
        this.newMessage = '';
        this.closeComponent.emit();
        this.toasterService.showSuccess(
          TIMESHEET_CONSTANTS.MESSAGES.COMMENT_SAVED_SUCCESS,
          TIMESHEET_CONSTANTS.TITLES.SUCCESS,
        );
      },
      error: (error) => {
        const errorMessage =
          error.error?.message ||
          error.message ||
          TIMESHEET_CONSTANTS.MESSAGES.FAILED_SAVE_COMMENT;
        this.toasterService.showError(
          errorMessage,
          TIMESHEET_CONSTANTS.TITLES.SAVE_ERROR,
        );
      },
    });
  }

  onViewChanged(): void {
    if (this.componentName === PersonaType.RtoContractorList) {
      return;
    }
    setTimeout(() => {
      const activeDate: Date = this.calendar?.activeDate;
      if (activeDate) {
        this.selectedmonth = activeDate.getMonth() + 1;
        this.selectedyear = activeDate.getFullYear();
        this.currentCalendarMonth = activeDate.getMonth() + 1;
        this.currentCalendarYear = activeDate.getFullYear();
        this.fetchLeaveSummary();
      }
    }, 0);
  }

  get headerTitleText(): string {
    return this.componentName === PersonaType.RtoContractorList
      ? HeaderText.RTO_Calendar
      : HeaderText.LeaveSummary;
  }

  get displayEmployeeName(): string {
    return (
      this.candidateDetails.employeeName ||
      this.candidateDetails.contractorName ||
      ''
    );
  }

  getTimesheetDetails(employeeId: any, month: any, year: any): void {
    this.vendorService.getTimesheetDetails(month, year, this.candidateDetails.vendorId).subscribe({
      next: (res: any) => {
        const found = res.data.find((ts: any) => ts.employeeId === employeeId);
        this.selectedEmployeeTimesheet = found;
        this.localBillingDates = found?.billingDates ?? null;
        this._cachedBillingKey = '';
        this.refreshBillingHighlight();
        this.calendar?.updateTodaysDate();
      },
      error: (error) => {},
    });
  }
}